/** Client-local active workspace id — runtime tenant for X-Trove-Aid header. */
export const CLIENT_ACTIVE_AID_KEY = 'trove_active_aid';
export const ACTIVE_AID_BROADCAST_CHANNEL = 'trove-active-aid';

function hasLocalStorage(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

function extensionRuntime():
  | { sendMessage: (msg: unknown, cb?: (response: unknown) => void) => void }
  | undefined {
  if (typeof window === 'undefined') return undefined;
  const runtime = (window as unknown as { chrome?: { runtime?: { sendMessage?: unknown } } })
    .chrome?.runtime;
  if (runtime && typeof runtime.sendMessage === 'function') {
    return runtime as { sendMessage: (msg: unknown, cb?: (response: unknown) => void) => void };
  }
  return undefined;
}

/** True when the React app runs inside the extension package (not app.trovepile.com). */
export function isExtensionEmbeddedApp(): boolean {
  return typeof window !== 'undefined' && window.location.protocol === 'chrome-extension:';
}

export function getClientActiveAid(): string | null {
  if (!hasLocalStorage()) return null;
  const aid = localStorage.getItem(CLIENT_ACTIVE_AID_KEY);
  return typeof aid === 'string' && aid.trim() !== '' ? aid : null;
}

function broadcastActiveAidChange(aid: string | null): void {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const bc = new BroadcastChannel(ACTIVE_AID_BROADCAST_CHANNEL);
    bc.postMessage({ type: 'ACTIVE_AID_CHANGED', aid });
    bc.close();
  } catch {
    // ignore
  }
}

/** Persist local workspace and sync extension service worker when present. */
export async function setClientActiveAid(aid: string): Promise<void> {
  if (!aid || aid.trim() === '') return;
  // In the embedded extension app, wait for the SW to finish aid switch/hydrate
  // before broadcasting — otherwise session reload races an empty IndexedDB.
  if (isExtensionEmbeddedApp()) {
    const runtime = extensionRuntime();
    if (runtime) {
      await new Promise<void>(resolve => {
        try {
          runtime.sendMessage({ action: 'SET_ACTIVE_AID', data: { aid } }, () => {
            void (window as unknown as { chrome?: { runtime?: { lastError?: unknown } } }).chrome
              ?.runtime?.lastError;
            resolve();
          });
        } catch {
          resolve();
        }
      });
    }
  }
  if (hasLocalStorage()) {
    localStorage.setItem(CLIENT_ACTIVE_AID_KEY, aid);
  }
  broadcastActiveAidChange(aid);
}

export function clearClientActiveAid(): void {
  if (hasLocalStorage()) {
    localStorage.removeItem(CLIENT_ACTIVE_AID_KEY);
  }
  broadcastActiveAidChange(null);
  if (!isExtensionEmbeddedApp()) return;
  const runtime = extensionRuntime();
  if (runtime) {
    try {
      runtime.sendMessage({ action: 'CLEAR_ACTIVE_AID' });
    } catch {
      // ignore
    }
  }
}

/** Seed localStorage from extension SW when empty (embedded app bootstrap). */
export async function hydrateClientActiveAidFromSw(): Promise<string | null> {
  const existing = getClientActiveAid();
  if (existing) return existing;
  if (!isExtensionEmbeddedApp()) return null;
  const runtime = extensionRuntime();
  if (!runtime) return null;
  const aid = await new Promise<string | null>(resolve => {
    try {
      runtime.sendMessage({ action: 'GET_ACTIVE_AID' }, response => {
        void (window as unknown as { chrome?: { runtime?: { lastError?: unknown } } }).chrome
          ?.runtime?.lastError;
        const body = response as { aid?: string } | undefined;
        const value = typeof body?.aid === 'string' && body.aid.trim() !== '' ? body.aid : null;
        resolve(value);
      });
    } catch {
      resolve(null);
    }
  });
  if (aid && hasLocalStorage()) {
    localStorage.setItem(CLIENT_ACTIVE_AID_KEY, aid);
  }
  return aid ?? getClientActiveAid();
}

/** Subscribe to workspace changes from other tabs (same origin). */
export function subscribeActiveAidChanges(
  callback: (aid: string | null) => void
): () => void {
  if (typeof window === 'undefined') return () => undefined;

  const onStorage = (event: StorageEvent) => {
    if (event.key === CLIENT_ACTIVE_AID_KEY) {
      callback(event.newValue);
    }
  };

  let bc: BroadcastChannel | null = null;
  const onBroadcast = (event: MessageEvent) => {
    const data = event.data as { type?: string; aid?: string | null } | undefined;
    if (data?.type === 'ACTIVE_AID_CHANGED') {
      callback(typeof data.aid === 'string' ? data.aid : null);
    }
  };

  window.addEventListener('storage', onStorage);
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      bc = new BroadcastChannel(ACTIVE_AID_BROADCAST_CHANNEL);
      bc.onmessage = onBroadcast;
    } catch {
      bc = null;
    }
  }

  return () => {
    window.removeEventListener('storage', onStorage);
    if (bc) {
      bc.close();
      bc = null;
    }
  };
}
